import { Component, inject, signal, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterLink } from '@angular/router';
import {
  TransactionAccessKey,
  TransactionAccessService
} from '../shared/services/transaction-access.service';

interface HomeCard {
  titulo: string;
  descripcion: string;
  icono: string;
  color: string;
  acciones: {
    texto: string;
    ruta: string;
    estilo: string;
    icono: string;
    accessKey: TransactionAccessKey;
  }[];
}

type HomeAction = HomeCard['acciones'][number];

@Component({
  selector: 'app-home',
  standalone: true,
  imports: [CommonModule, RouterLink],
  templateUrl: './home.html',
  styleUrls: ['./home.scss']
})
export class Home implements OnInit {
  private readonly transactionAccess = inject(TransactionAccessService);
  private readonly router = inject(Router);
  currentYear = new Date().getFullYear();
  usuario = signal<string>('Invitado');

  cards = signal<HomeCard[]>([]);
  maintenanceActions = signal<HomeAction[]>([]);

  ngOnInit(): void {
    const savedUser = localStorage.getItem('authUser');
    if (savedUser) {
      const user = JSON.parse(savedUser);
      this.usuario.set(user.nombre);
    }

    this.cargarCards();
  }

  openSingleActionCard(card: HomeCard): void {
    if (card.acciones.length === 1) {
      this.router.navigateByUrl(card.acciones[0].ruta);
    }
  }

  handleCardKeydown(event: KeyboardEvent, card: HomeCard): void {
    if (card.acciones.length === 1 && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this.openSingleActionCard(card);
    }
  }

  private cargarCards(): void {
    const listado: HomeCard[] = [
      {
        titulo: 'Mantenimientos',
        descripcion: 'Catálogos y mantenimientos asignados al usuario.',
        icono: 'bi-gear',
        color: 'primary',
        acciones: [
          {
            texto: 'Actividades de Empresa',
            ruta: '/actividades-empresa',
            estilo: 'outline-primary',
            icono: 'bi-briefcase',
            accessKey: 'businessActivities'
          },
          {
            texto: 'Tamaños de Empresa',
            ruta: '/tamanos-empresa',
            estilo: 'outline-primary',
            icono: 'bi-arrows-angle-expand',
            accessKey: 'companySizes'
          },
          {
            texto: 'Unidades de Medida',
            ruta: '/unidades-medida',
            estilo: 'outline-primary',
            icono: 'bi-rulers',
            accessKey: 'measurementUnits'
          },
          {
            texto: 'Periodicidades de Visita',
            ruta: '/periodicidades-visita',
            estilo: 'outline-primary',
            icono: 'bi-calendar-range',
            accessKey: 'visitFrequencies'
          },
          {
            texto: 'Razones NT',
            ruta: '/razones-nt',
            estilo: 'outline-primary',
            icono: 'bi-chat-square-text',
            accessKey: 'nonResponseReasons'
          },
          {
            texto: 'Características',
            ruta: '/caracteristicas',
            estilo: 'outline-primary',
            icono: 'bi-ui-checks-grid',
            accessKey: 'characteristics'
          },
          {
            texto: 'Valores de Características',
            ruta: '/caracteristicas/valores',
            estilo: 'outline-primary',
            icono: 'bi-list-ul',
            accessKey: 'characteristicValues'
          },
          {
            texto: 'Razones de Variación',
            ruta: '/razones-variacion',
            estilo: 'outline-primary',
            icono: 'bi-graph-up-arrow',
            accessKey: 'variationReasons'
          },
          {
            texto: 'Relación Usuario – Transacción',
            ruta: '/transacciones-usuarios',
            estilo: 'outline-primary',
            icono: 'bi-person-check',
            accessKey: 'userTransactions'
          },
          {
            texto: 'Transacciones',
            ruta: '/transacciones',
            estilo: 'outline-primary',
            icono: 'bi-list-check',
            accessKey: 'transactions'
          },
          {
            texto: 'Usuarios',
            ruta: '/usuarios',
            estilo: 'outline-primary',
            icono: 'bi-people',
            accessKey: 'users'
          },
          { texto: 'Artículos', ruta: '/search-article', estilo: 'outline-success', icono: 'bi-box-seam', accessKey: 'articles' },
          { texto: 'Fuentes', ruta: '/search-source', estilo: 'outline-primary', icono: 'bi-building', accessKey: 'sources' },
          { texto: 'Productos', ruta: '/search-product', estilo: 'outline-info', icono: 'bi-cart4', accessKey: 'products' },
          { texto: 'Variedades', ruta: '/search-variety', estilo: 'outline-warning', icono: 'bi-tags', accessKey: 'varieties' }
        ]
      },
      {
        titulo: 'Levantamiento de Fuentes',
        descripcion: 'Registro y actualización de información de las fuentes.',
        icono: 'bi-building-up',
        color: 'primary',
        acciones: [
          {
            texto: 'Levantar fuente',
            ruta: '/source-fieldwork',
            estilo: 'primary',
            icono: 'bi-plus-circle',
            accessKey: 'sourceFieldwork'
          }
        ]
      },
      {
        titulo: 'Administración',
        descripcion: 'Consulta de eventos y acciones realizadas en el sistema.',
        icono: 'bi-shield-check',
        color: 'primary',
        acciones: [
          {
            texto: 'Bitácora',
            ruta: '/bitacora',
            estilo: 'primary',
            icono: 'bi-journal-text',
            accessKey: 'audit'
          }
        ]
      },
      {
        titulo: 'Reportes',
        descripcion: 'Generación y exportación de información del sistema.',
        icono: 'bi-bar-chart-line',
        color: 'primary',
        acciones: [
          {
            texto: 'Exportar datos',
            ruta: '/export-data',
            estilo: 'primary',
            icono: 'bi-file-earmark-arrow-down',
            accessKey: 'exportData'
          }
        ]
      },
      {
        titulo: 'Cálculos',

        descripcion: 'Módulo de cálculo y consulta.',
        icono: 'bi-calculator',
        color: 'dark',
        acciones: [
          { texto: 'Consultar', ruta: '/calculos', estilo: 'outline-primary', icono: 'bi-search', accessKey: 'calculations' },
          { texto: 'Ejecutar', ruta: '/calculos/ejecutar', estilo: 'primary', icono: 'bi-gear-fill', accessKey: 'calculationExecution' }
        ]
      },
      {
        titulo: 'Boletas',
        descripcion: 'Transacción de gestion de boletas',
        icono: 'bi bi-file-text-fill',
        color: 'secondary',
        acciones: [
          { texto: 'Captura', ruta: '/ballot-capture', estilo: 'outline-primary', icono: 'bi-clipboard-check', accessKey: 'ballotCapture' },
          { texto: 'Editar', ruta: '/ballot-editing', estilo: 'primary', icono: 'bi-pencil', accessKey: 'ballotEditing' },
          { texto: 'Revisión', ruta: '/revision-boletas', estilo: 'primary', icono: 'bi-clipboard2-check', accessKey: 'ballotReview' },
          { texto: 'Consulta', ruta: '/consulta-boletas', estilo: 'primary', icono: 'bi-search', accessKey: 'ballotQuery' }

        ]
      }

    ];

    const maintenanceCard = listado.find((card) => card.titulo === 'Mantenimientos');
    this.maintenanceActions.set(
      (maintenanceCard?.acciones ?? [])
        .filter((action) => this.transactionAccess.hasAccess(action.accessKey, action.ruta))
        .sort((a, b) => a.texto.localeCompare(b.texto, 'es', { sensitivity: 'base' }))
    );

    const authorizedCards = listado
      .filter((card) => card.titulo !== 'Mantenimientos')
      .map((card) => ({
        ...card,
        acciones: card.acciones.filter((action) =>
          this.transactionAccess.hasAccess(action.accessKey, action.ruta)
        )
      }))
      .filter((card) => card.acciones.length > 0)
      .sort((a, b) => a.titulo.localeCompare(b.titulo));

    this.cards.set(authorizedCards);
  }
}
